import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { assetsApi, peopleApi, physicalSitesApi } from '../services/api';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { DocumentManager } from '../components/DocumentManager';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { totalAnnualSpendUsd, formatUsd } from '../utils/assetSpend';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Asset = {
  assetId: string;
  name?: string;
  type?: string;
  description?: string;
  owner?: string;
  location?: string | string[];
  parentAssetId?: string | null;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchaseCost?: number;
  purchaseCurrency?: string;
  billingEntries?: { id: string; date: string; currency?: string; amount?: number }[];
  classification?: string;
  criticality?: string;
  status?: string;
  notes?: string;
};

type BillingRow = { id: string; date: string; currency: string; amount: string };

// Small, deliberately short list rather than the full ISO-4217 set - matches the currencies
// already seen in this tenant's real spend data (£/€ on the tracker's own metrics), plus USD
// and Mercell's Nordic home markets. Not exhaustive by design: "a small box".
const CURRENCIES = ['GBP', 'EUR', 'USD', 'NOK', 'SEK', 'DKK', 'CHF'];

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  personName?: string;
};

const getPersonName = (p: Person): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return (full || p.personName || p.personId).trim();
};

const EMPTY_FORM = {
  name: '',
  type: '',
  description: '',
  owner: '',
  location: [] as string[],
  parentAssetId: '',
  serialNumber: '',
  manufacturer: '',
  model: '',
  purchaseDate: '',
  billingEntries: [] as BillingRow[],
  classification: 'internal',
  criticality: 'Medium',
  status: 'active',
  notes: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AssetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const isEdit = !!id && id !== 'new';
  const assetId = isEdit ? id : null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState<'details' | 'attachments' | 'raci' | 'artefacts'>('details');

  // ── Fetch existing asset ────────────────────────────────────────────────
  const { data: assetData, isLoading: assetLoading } = useQuery<any>({
    queryKey: ['asset', id],
    queryFn: () => assetsApi.getOne(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!assetData) return;
    const a: Asset = assetData.asset ?? assetData;
    setForm({
      name: a.name || '',
      type: a.type || '',
      description: a.description || '',
      owner: a.owner || '',
      // Older assets stored a single site ID as a plain string -- normalise to an array
      // now that a product can be hosted across multiple locations (e.g. multi-region cloud).
      location: Array.isArray(a.location) ? a.location : a.location ? [a.location] : [],
      parentAssetId: a.parentAssetId || '',
      serialNumber: a.serialNumber || '',
      manufacturer: a.manufacturer || '',
      model: a.model || '',
      purchaseDate: a.purchaseDate || '',
      billingEntries: (a.billingEntries || []).map((b) => ({
        id: b.id,
        date: b.date || '',
        currency: b.currency || 'USD',
        amount: b.amount != null ? String(b.amount) : '',
      })),
      classification: a.classification || 'internal',
      criticality: a.criticality || 'Medium',
      status: a.status || 'active',
      notes: a.notes || '',
    });
  }, [assetData]);

  // ── People for owner dropdown ───────────────────────────────────────────
  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const ownerOptions = useMemo(
    () => people.map((p) => ({ value: p.personId, label: getPersonName(p) })).sort((a, b) => a.label.localeCompare(b.label)),
    [people],
  );

  // ── Physical sites for location dropdown ────────────────────────────────
  const { data: physicalSites = [] } = useQuery<any[]>({
    queryKey: ['physicalSites'],
    queryFn: physicalSitesApi.getAll,
  });

  const siteOptions = useMemo(
    () => physicalSites.map((s) => ({ value: s.siteId, label: s.name || s.siteId })).sort((a, b) => a.label.localeCompare(b.label)),
    [physicalSites],
  );

  // ── Other assets, for the Parent Asset dropdown ─────────────────────────
  const { data: allAssets = [] } = useQuery<Asset[]>({
    queryKey: ['assets'],
    queryFn: assetsApi.getAll,
  });

  // An asset can't be parented under itself or any of its own descendants --
  // walk the tree from this asset to find every ID that must be excluded.
  const excludedParentIds = useMemo(() => {
    const excluded = new Set<string>();
    if (!assetId) return excluded;
    excluded.add(assetId);
    const childrenByParent = new Map<string, Asset[]>();
    allAssets.forEach((a) => {
      if (a.parentAssetId) {
        if (!childrenByParent.has(a.parentAssetId)) childrenByParent.set(a.parentAssetId, []);
        childrenByParent.get(a.parentAssetId)!.push(a);
      }
    });
    const queue = [assetId];
    while (queue.length) {
      const current = queue.shift()!;
      (childrenByParent.get(current) || []).forEach((child) => {
        if (!excluded.has(child.assetId)) {
          excluded.add(child.assetId);
          queue.push(child.assetId);
        }
      });
    }
    return excluded;
  }, [allAssets, assetId]);

  const parentAssetOptions = useMemo(
    () => allAssets
      .filter((a) => !excludedParentIds.has(a.assetId))
      .map((a) => ({ value: a.assetId, label: a.name || a.assetId }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [allAssets, excludedParentIds],
  );

  // ── Billing rows (Spend card) ────────────────────────────────────────────
  const addBillingRow = () => {
    setForm((f) => {
      // Default to whatever currency the last row used, or USD for the very first row -- most
      // assets bill in one currency throughout, so this saves re-picking it every time.
      const defaultCurrency = f.billingEntries[f.billingEntries.length - 1]?.currency || 'USD';
      return { ...f, billingEntries: [...f.billingEntries, { id: `BILL-${Date.now()}`, date: '', currency: defaultCurrency, amount: '' }] };
    });
  };
  const updateBillingRow = (rowId: string, patch: Partial<BillingRow>) => {
    setForm((f) => ({ ...f, billingEntries: f.billingEntries.map((b) => (b.id === rowId ? { ...b, ...patch } : b)) }));
  };
  const removeBillingRow = (rowId: string) => {
    setForm((f) => ({ ...f, billingEntries: f.billingEntries.filter((b) => b.id !== rowId) }));
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  // Live, computed from whatever's currently in the form (including unsaved edits) - not a
  // stored field. Purchase Cost is a one-off and deliberately excluded; see assetSpend.ts.
  const totalAnnualSpend = totalAnnualSpendUsd(
    form.billingEntries
      .filter((b) => b.date && b.amount.trim() !== '')
      .map((b) => ({ date: b.date, currency: b.currency, amount: parseFloat(b.amount) })),
  );

  // ── Mutations ───────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        name: form.name,
        type: form.type || null,
        description: form.description || null,
        owner: form.owner || null,
        location: form.location.length ? form.location : null,
        parentAssetId: form.parentAssetId || null,
        serialNumber: form.serialNumber || null,
        manufacturer: form.manufacturer || null,
        model: form.model || null,
        purchaseDate: form.purchaseDate || null,
        // purchaseCost/purchaseCurrency are deliberately omitted, not zeroed - the backend's
        // update only touches fields present in the body, so this leaves whatever an asset had
        // from before the Spend card was redesigned untouched rather than overwriting it to 0.
        // Rows with no date typed yet are dropped rather than saved half-filled -- a blank
        // amount is fine (billed but not yet costed out), a blank date isn't a real entry.
        billingEntries: form.billingEntries
          .filter((b) => b.date)
          .map((b) => ({ id: b.id, date: b.date, currency: b.currency, amount: b.amount.trim() === '' ? undefined : parseFloat(b.amount) })),
        classification: form.classification,
        criticality: form.criticality,
        status: form.status,
        notes: form.notes || null,
      };
      if (isEdit) {
        await assetsApi.update(id!, payload);
      } else {
        const result = await assetsApi.create(payload);
        return result;
      }
    },
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      if (!isEdit && result) {
        const newId = result.asset?.assetId ?? result.assetId;
        toast.success('Asset created.');
        if (newId) {
          navigate(`/assets/${newId}/edit`, { replace: true });
          return;
        }
      }
      toast.success(isEdit ? 'Asset updated.' : 'Asset created.');
      if (!isEdit) navigate('/assets');
    },
    onError: (e: Error) => toast.error(`Failed to save: ${e.message}`),
  });

  // ── Render ──────────────────────────────────────────────────────────────

  if (isEdit && assetLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-server-line"
        title={isEdit ? 'Edit Asset' : 'New Asset'}
        colour="warning"
      />

      {/* ID subtitle */}
      {isEdit && (
        <div className="mb-3 d-flex align-items-center gap-2">
          <span className="np-chip np-tone-warning np-tabular">{id}</span>
          <span className="np-sub">{form.name}</span>
        </div>
      )}

      {/* Tab nav */}
      <div className="np-tabs" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-file-list-3-line"></i>Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'attachments' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('attachments')}
        >
          <i className="ri-attachment-2"></i>Attachments
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'raci' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('raci')}
        >
          <i className="ri-team-line"></i>RACI
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {/* ── Details tab ──────────────────────────────────────────── */}
      {activeTab === 'details' && (
        <div className="row">
          <div className="col-12 mb-4">
            <div className="np-card">
              <div className="np-card-head">
                <h2><i className="ri-file-list-3-line"></i> Asset Details</h2>
              </div>
              <div className="np-card-body">
                <div className="row g-3">
                  <div className="col-12 col-md-6 np-field">
                    <label>Name <span className="text-danger">*</span></label>
                    <input
                      className="np-input"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Production Web Server"
                    />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Type</label>
                    <select
                      className="np-select"
                      value={form.type}
                      onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                    >
                      <option value="">Select type…</option>
                      <optgroup label="Information">
                        <option>Database</option>
                        <option>File Share</option>
                        <option>Cloud Storage</option>
                        <option>Email Archive</option>
                        <option>Physical Records</option>
                        <option>Backup Data</option>
                      </optgroup>
                      <optgroup label="Software">
                        <option>Operating System</option>
                        <option>Business Application</option>
                        <option>Development Tool</option>
                        <option>Security Tool</option>
                        <option>Cloud Service (SaaS)</option>
                        <option>Open Source Library</option>
                        <option>Internal Product</option>
                      </optgroup>
                      <optgroup label="Hardware">
                        <option>Server</option>
                        <option>Workstation</option>
                        <option>Laptop</option>
                        <option>Mobile Device</option>
                        <option>Network Device</option>
                        <option>Storage Device</option>
                        <option>IoT Device</option>
                        <option>Printer / Peripheral</option>
                      </optgroup>
                      <optgroup label="Services">
                        <option>Cloud Infrastructure (IaaS)</option>
                        <option>Managed Service</option>
                        <option>Utility Service</option>
                        <option>Communication Service</option>
                      </optgroup>
                      <optgroup label="People">
                        <option>Employee</option>
                        <option>Contractor</option>
                        <option>Third Party Supplier</option>
                      </optgroup>
                      <optgroup label="Facilities">
                        <option>Office</option>
                        <option>Data Centre</option>
                        <option>Server Room</option>
                        <option>Disaster Recovery Site</option>
                      </optgroup>
                    </select>
                  </div>
                  <div className="col-12 np-field">
                    <label>Description</label>
                    <textarea
                      className="np-input"
                      rows={2}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Owner</label>
                    <Select
                      options={ownerOptions}
                      styles={reactSelectStyles()}
                      isClearable
                      placeholder="Search owner…"
                      value={ownerOptions.find((o) => o.value === form.owner) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, owner: opt?.value || '' }))}
                    />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Location</label>
                    <Select
                      options={siteOptions}
                      styles={reactSelectStyles()}
                      isClearable
                      isMulti
                      placeholder="Search location(s)…"
                      value={siteOptions.filter((o) => form.location.includes(o.value))}
                      onChange={(opts) => setForm((f) => ({ ...f, location: (opts || []).map((o) => o.value) }))}
                    />
                    <small className="np-hint">Select multiple locations for assets hosted across more than one site (e.g. multi-region cloud).</small>
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Parent Asset</label>
                    <Select
                      options={parentAssetOptions}
                      styles={reactSelectStyles()}
                      isClearable
                      placeholder="Search parent asset…"
                      value={parentAssetOptions.find((o) => o.value === form.parentAssetId) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, parentAssetId: opt?.value || '' }))}
                    />
                    <small className="np-hint">Optional — nest this asset under another to build a hierarchy (e.g. a VM under its host server).</small>
                  </div>
                  <div className="col-12 col-md-4 np-field">
                    <label>Serial Number</label>
                    <input
                      className="np-input"
                      value={form.serialNumber}
                      onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 col-md-4 np-field">
                    <label>Manufacturer</label>
                    <input
                      className="np-input"
                      value={form.manufacturer}
                      onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 col-md-4 np-field">
                    <label>Model</label>
                    <input
                      className="np-input"
                      value={form.model}
                      onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 col-md-4 np-field">
                    <label>Classification</label>
                    <select
                      className="np-select"
                      value={form.classification}
                      onChange={(e) => setForm((f) => ({ ...f, classification: e.target.value }))}
                    >
                      <option value="public">public</option>
                      <option value="internal">internal</option>
                      <option value="confidential">confidential</option>
                      <option value="restricted">restricted</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-4 np-field">
                    <label>Criticality</label>
                    <select
                      className="np-select"
                      value={form.criticality}
                      onChange={(e) => setForm((f) => ({ ...f, criticality: e.target.value }))}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                    <small className="np-hint">Business impact if this asset is compromised. Feeds into inherent risk for any risk linked to this asset.</small>
                  </div>
                  <div className="col-12 col-md-4 np-field">
                    <label>Status</label>
                    <select
                      className="np-select"
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="retired">retired</option>
                      <option value="maintenance">maintenance</option>
                    </select>
                  </div>
                  <div className="col-12 np-field">
                    <label>Notes</label>
                    <textarea
                      className="np-input"
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mb-4">
            <div className="np-card">
              <div className="np-card-head">
                <h2><i className="ri-money-dollar-circle-line"></i> Spend</h2>
              </div>
              <div className="np-card-body">
                <p className="np-field-caption">Purchase</p>
                <div className="row g-3 mb-2">
                  <div className="col-12 col-md-4 np-field">
                    <label>Purchase Date</label>
                    <input
                      className="np-input"
                      type="date"
                      value={form.purchaseDate}
                      onChange={(e) => setForm((f) => ({ ...f, purchaseDate: e.target.value }))}
                    />
                  </div>
                </div>

                <hr />

                <div className="d-flex align-items-center gap-2 mb-1">
                  <p className="np-field-caption mb-0">Billing</p>
                  {form.billingEntries.some((b) => b.date && b.date > todayIso) && (
                    <span className="np-chip np-tone-warning ms-1">
                      {form.billingEntries.filter((b) => b.date && b.date > todayIso).length} upcoming
                    </span>
                  )}
                  <span className="ms-auto text-end">
                    <span className="np-sub d-block" style={{ fontSize: '0.68rem' }}>Total Annual Spend</span>
                    <span className="fw-bold" style={{ fontSize: '0.95rem' }}>{formatUsd(totalAnnualSpend)}</span>
                  </span>
                </div>
                <small className="np-hint d-block mb-2">Every date billing was — or will be — collected for this asset. Total Annual Spend sums this calendar year's billing rows, converted to USD at a static approximate rate — not a live exchange rate.</small>

                {form.billingEntries.length === 0 && (
                  <p className="np-sub small mb-2">No billing dates recorded yet.</p>
                )}
                {form.billingEntries.map((b) => {
                  const isPaid = !!b.date && b.date <= todayIso;
                  return (
                    <div key={b.id} className="row g-2 align-items-end mb-2 pb-2" style={{ borderBottom: '1px solid var(--np-border)' }}>
                      <div className="col-6 col-md-3 np-field mb-0">
                        <label>Date</label>
                        <input
                          className="np-input"
                          type="date"
                          value={b.date}
                          onChange={(e) => updateBillingRow(b.id, { date: e.target.value })}
                        />
                      </div>
                      <div className="col-6 col-md-2 np-field mb-0">
                        <label>Currency</label>
                        <select
                          className="np-select"
                          value={b.currency}
                          onChange={(e) => updateBillingRow(b.id, { currency: e.target.value })}
                        >
                          {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="col-5 col-md-3 np-field mb-0">
                        <label>Amount</label>
                        <input
                          className="np-input"
                          type="number"
                          placeholder="Optional"
                          value={b.amount}
                          onChange={(e) => updateBillingRow(b.id, { amount: e.target.value })}
                        />
                      </div>
                      <div className="col-auto">
                        {b.date && (
                          <span className={`np-chip np-tone-${isPaid ? 'success' : 'warning'}`}>{isPaid ? 'Paid' : 'Upcoming'}</span>
                        )}
                      </div>
                      <div className="col-auto ms-auto">
                        <button
                          type="button"
                          className="np-icon-btn np-icon-btn-danger"
                          title="Remove"
                          onClick={() => removeBillingRow(b.id)}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button type="button" className="np-btn np-btn-ghost np-btn-sm mt-1" onClick={addBillingRow}>
                  <i className="ri-add-line"></i>Add billing date
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Attachments tab ───────────────────────────────────────── */}
      {activeTab === 'attachments' && (
        <div className="row">
          {assetId ? (
            <DocumentManager entityId={assetId} entityType="asset" />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-attachment-2"></i> Attachments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Save this asset first, then come back to attach documents.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── RACI tab ──────────────────────────────────────────────── */}
      {activeTab === 'raci' && (
        <div className="row">
          {assetId ? (
            <RaciAssignments artefactId={assetId} artefactType="asset" />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-team-line"></i> RACI Assignments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Save this asset first, then come back to assign RACI roles.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Linked Artefacts tab ──────────────────────────────────── */}
      {activeTab === 'artefacts' && (
        <div className="row">
          {assetId ? (
            <LinkedArtefacts
              artefactId={assetId}
              artefactType="asset"
              artefactRegister="assets"
            />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-links-line"></i> Linked Artefacts</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Save this asset first, then come back to link artefacts.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom action bar */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="np-btn np-btn-ghost" onClick={() => navigate('/assets')}>
          <i className="ri-close-line"></i>Cancel
        </button>
        <button
          className="np-btn np-btn-primary"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !form.name.trim()}
        >
          {saveMutation.isPending
            ? <><span className="spinner-border spinner-border-sm"></span>Saving…</>
            : isEdit ? 'Save Changes' : 'Create Asset'}
        </button>
      </div>
    </div>
  );
};
